import { getAppConfig, isDebugEnabled } from '../utils/config';
import { logger } from "../utils/logger";

import { request } from './request';

// Create component logger for image edit service
const imageEditLogger = logger.withComponent("ImageEditService");

interface ImageEditChatResponse {
  response_id: string;
  session_id: string;
  created: number;
  data: Array<{
    base64: string;
  }>;
}

interface ImageEditResponse {
  created: number;
  image_b64: string;
  usage: {
    total_tokens: number;
    input_tokens: number;
    output_tokens: number;
  };
}

interface ImageEditOptions {
  abortSignal?: AbortSignal;
  timeout?: number; // milliseconds
  retries?: number; // number of retry attempts
}

// Configuration for image edit requests
const IMAGE_EDIT_CONFIG = {
  size: 'auto' as const,
  quality: 'low' as const,
  maxFileSize: 5 * 1024 * 1024, // 5MB
  validTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'] as const,
} as const;

export async function editImage(
  file: File,
  prompt: string,
  options: ImageEditOptions = {},
): Promise<ImageEditChatResponse> {
  // Client-side validation
  if (!IMAGE_EDIT_CONFIG.validTypes.includes(file.type as any)) {
    throw new Error('Invalid file type. Supported: PNG, JPEG, WEBP, GIF');
  }
  if (file.size > IMAGE_EDIT_CONFIG.maxFileSize) {
    throw new Error(
      `File too large. Maximum size: ${IMAGE_EDIT_CONFIG.maxFileSize / 1024 / 1024}MB`,
    );
  }

  // Get API base URL from configuration
  const config = getAppConfig();
  const endpoint = `${config.apiBaseUrl}/api/images/edit`;

  // Build FormData
  const formData = new FormData();
  formData.append('image', file); // Note: 'image' not 'file'
  formData.append('prompt', prompt);

  // Optional parameters from config
  formData.append('size', IMAGE_EDIT_CONFIG.size);
  formData.append('quality', IMAGE_EDIT_CONFIG.quality);

  // Log request in debug mode
  if (isDebugEnabled()) {
    imageEditLogger.info('🎨 Image Edit Request:', {
      endpoint,
      fileName: file.name,
      fileSize: `${(file.size / 1024).toFixed(2)} KB`,
      fileType: file.type,
      prompt: prompt.substring(0, 50) + (prompt.length > 50 ? '...' : ''),
    });
  }

  try {
    const data = await request<ImageEditResponse>(endpoint, {
      method: 'POST',
      body: formData,
      signal: options.abortSignal,
      timeout: options.timeout,
      retries: options.retries,
    });

    // Log response in debug mode
    if (isDebugEnabled()) {
      imageEditLogger.info('✅ Image Edit Response:', {
        created: data.created,
        imageSize: data.image_b64.length,
        usage: data.usage,
      });
    }

    // Transform to our standard ChatResponse format
    return {
      response_id: 'edit_' + Date.now(),
      session_id: 'image_edit', // Static, no session tracking
      created: data.created,
      data: [
        {
          base64: `data:image/png;base64,${data.image_b64}`,
        },
      ],
    };
  } catch (error) {
    imageEditLogger.error('❌ Image Edit Error:', error);
    throw error;
  }
}